import React from "react";

import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";

const pounds = (minor: number) => `£${(minor / 100).toFixed(2)}`;

/** The pence for what was typed, or null when it isn't pounds and pence (at most two decimal places). */
const penceOf = (text: string): number | null => {
	const trimmed = text.trim();
	if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return null;
	return Math.round(Number(trimmed) * 100);
};

/**
 * HA-858 (S1, AC4) — a manual tip on the payment screen: an amount above £0 and up to what's owed, with the rule shown when
 * it isn't. Applied, it rides on the next tender (the cash or card payment carries it as its own tip, and the reader then
 * skips its own tip screen). Not shown at all while the venue takes no tips: the server would refuse one (TIPS_DISABLED).
 */
export function AddTip({
	enabled,
	owed,
	tip,
	onChange,
}: {
	enabled: boolean;
	/** What the order (or split) still owes: the most a tip can be. */
	owed: { value: number; currency: string };
	/** The tip applied now, in pence; 0 for none. */
	tip: number;
	onChange: (minor: number) => void;
}) {
	const [editing, setEditing] = React.useState(false);
	const [text, setText] = React.useState("");
	const [problem, setProblem] = React.useState<string | null>(null);

	if (!enabled) return null;

	const rule = `A tip is more than £0.00 and at most ${pounds(owed.value)}, in pounds and pence.`;

	const apply = () => {
		const pence = penceOf(text);
		if (pence === null || pence <= 0 || pence > owed.value) {
			setProblem(rule);
			return;
		}

		setProblem(null);
		setEditing(false);
		setText("");
		onChange(pence);
	};

	if (tip > 0 && !editing) {
		return (
			<div className="flex items-center justify-between border-b p-4">
				<span className="text-sm font-medium">{`Tip ${pounds(tip)}`}</span>
				<Button variant="outline" size="sm" onClick={() => onChange(0)}>
					Remove tip
				</Button>
			</div>
		);
	}

	if (!editing) {
		return (
			<div className="border-b p-4">
				<Button
					variant="outline"
					className="w-full"
					onClick={() => setEditing(true)}
				>
					Add tip
				</Button>
			</div>
		);
	}

	return (
		<form
			className="space-y-2 border-b p-4"
			onSubmit={(event) => {
				event.preventDefault();
				apply();
			}}
		>
			<Label htmlFor="add-tip-amount">Tip amount (£)</Label>
			<div className="flex gap-2">
				<Input
					id="add-tip-amount"
					inputMode="decimal"
					value={text}
					onChange={(event) => setText(event.target.value)}
					aria-invalid={problem ? true : undefined}
				/>
				<Button type="submit">Apply tip</Button>
				<Button
					type="button"
					variant="outline"
					onClick={() => {
						setEditing(false);
						setProblem(null);
						setText("");
					}}
				>
					Cancel
				</Button>
			</div>
			{problem && (
				<p role="alert" className="text-sm text-destructive">
					{problem}
				</p>
			)}
		</form>
	);
}
